 
import { useRef, useState } from 'react';
import { Node } from '@xyflow/react';
import { useParams } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import useApi from 'hooks/useApi';
import OptionImpactDialog, {
  OptionImpactEntry,
  OptionImpactRule,
  OptionImpactRuleCondition
} from '../OptionImpactDialog';
import VideoForm from './Forms/videoForm';
import VideoCallForm from './Forms/VideoCallForm';
import SignForm from './Forms/SignForm';
import ImagesForm from './Forms/ImageForm';
import DocumentForm from './Forms/DocumentForm';
import InstructionsForm from './Forms/Instructions';
import DriverForm from './Forms/DriverForm';
import RedirectForm from './Forms/RedirectForm';
import StatusTableManager from './Forms/otherComponent/StatusTableManager';
import ReportTableManager from './Forms/otherComponent/ReportTableManager';

import { IImages, IVideo, ISESSION, IMaps, ISign, IINSTRUCTIONS } from 'types/auth';

import FormComponent from './Forms/FormComponents';
import ChecklistComponent from './Forms/ChecklistComponents';

type SidebarDrawerProps = {
  open: boolean;
  onClose: () => void;
  selectedNode: Node<any> | null;
  handleStepComponentData: (id: string, data: any) => void;
  extraNode: 'status' | 'report' | 'roles' | null;
  disabled?: boolean;
};

type RenderFormProps = {
  selectedNodeData: any;
  selectedNode: any;
  formRef: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  onStepTypeChange: (newType: string, shouldClearFields?: boolean) => void;
  disabled: boolean;
};

/**
 * Declared at module scope on purpose.
 *
 * This used to live inside SidebarDrawer, which made it a brand-new component type on every
 * render — React cannot match a new type to the old tree, so it unmounted and remounted the whole
 * form each time the parent re-rendered, throwing away Formik's in-progress state. The page
 * re-renders more often now that save status is tracked, so the form is hoisted out and only
 * re-renders (cheap, state preserved) rather than remounting.
 */
function RenderForm({ selectedNodeData, selectedNode, formRef, onSubmit, onClose, onStepTypeChange, disabled }: RenderFormProps) {
  const stepData = selectedNode?.data;
  const formProps = {
    selectedNodeData,
    selectedNode,
    onSubmit,
    onClose,
    onStepTypeChange,
    disabled
  };

  switch (stepData?.step?.stepType) {
    case 'IMAGE':
      return <ImagesForm {...formProps} />;
    case 'VIDEO':
      return <VideoForm {...formProps} />;
    case 'ESIGN':
      return <SignForm {...formProps} />;
    case 'SESSION':
      return <VideoCallForm {...formProps} />;
    case 'DOCUMENT':
      return <DocumentForm {...formProps} selectNode={selectedNode} />;
    case 'FORM':
      return <FormComponent ref={formRef} {...formProps} />;
    case 'CHECKLIST':
      return <ChecklistComponent {...formProps} />;
    case 'INSTRUCTION':
    case 'INSTRUCTIONS':
      return <InstructionsForm {...formProps} />;
    case 'DRIVER':
      return <DriverForm {...formProps} />;
    case 'REDIRECT':
      return <RedirectForm {...formProps} />;
    default:
      return <div style={{ padding: '1rem' }}>No form available for this step.</div>;
  }
}

export default function SidebarDrawer({
  open,
  onClose,
  selectedNode: selectedNodeProp,
  handleStepComponentData,
  extraNode,
  disabled = false
}: SidebarDrawerProps) {

  const formRef = useRef<any>(null);

  const { teamUid } = useParams<{ teamUid: string }>();
  const { getRulesByTeamUid } = useApi();

  // Non-empty while the option-impact dialog is up. The drawer stays open behind it and
  // the pending submit is held back, so cancelling leaves the edit in the form unsaved
  // rather than half-applied.
  const [optionImpact, setOptionImpact] = useState<OptionImpactEntry[]>([]);
  const pendingSubmitRef = useRef<any>(null);

  const commitFormData = (data: any) => {
    if (!selectedNodeProp) return;
    handleStepComponentData(selectedNodeProp.id, data);
    onClose();
  };

  /**
   * Rules this save would orphan.
   *
   * A rule condition points at its step and field by id, but stores the chosen option as
   * literal text — so an option that disappears from a field leaves every condition
   * holding it comparing against text no answer can carry again. Nothing errors; the
   * rule just stops matching.
   *
   * Comparing the options being saved against the values the rules actually hold answers
   * this directly, with no need to know which option was renamed into which. Read-only:
   * the rules are reported, never touched.
   */
  const findImpactedRules = async (data: any): Promise<OptionImpactEntry[]> => {
    const optionFields = (Array.isArray(data?.fields) ? data.fields : []).filter(
      (field: any) => field?.uid && Array.isArray(field?.options)
    );

    if (!teamUid || optionFields.length === 0) return [];

    // Only an option that disappears can orphan a rule, so a save that left every
    // option list alone needs no rules lookup at all — closing a drawer submits the
    // form whether or not anything was typed, and that is most saves.
    const storedFields = Array.isArray((selectedNodeProp?.data as any)?.component?.fields)
      ? (selectedNodeProp!.data as any).component.fields
      : [];

    const optionsChanged = optionFields.some((field: any) => {
      const storedField = storedFields.find((stored: any) => stored?.uid === field.uid);
      if (!storedField) return false;

      const storedOptions = (Array.isArray(storedField.options) ? storedField.options : []).map(String);
      const nextOptions = (field.options || []).map(String);

      return storedOptions.some((option: string) => !nextOptions.includes(option));
    });

    if (!optionsChanged) return [];

    const response = await getRulesByTeamUid(teamUid);
    const rules = Array.isArray(response?.data) ? response.data : [];
    if (rules.length === 0) return [];

    const impacted: OptionImpactEntry[] = [];

    optionFields.forEach((field: any) => {
      const options = (field.options || []).map((option: any) => String(option));
      const removed = new Set<string>();
      const affectedRules: OptionImpactRule[] = [];

      rules.forEach((rule: any) => {
        const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];

        const orphaned = conditions
          .filter((condition: any) => {
            if (condition?.field !== field.uid) return false;

            const value = condition?.value;
            // '' is "not configured yet" and qs-undefined is the explicit No Value
            // sentinel — neither points at an option, so neither can be orphaned.
            if (!value || value === 'qs-undefined') return false;

            return !options.includes(String(value));
          })
          .map((condition: any) => ({
            expression: condition?.expression || '==',
            value: String(condition?.value)
          }));

        if (orphaned.length === 0) return;

        orphaned.forEach((condition: OptionImpactRuleCondition) => removed.add(condition.value));
        affectedRules.push({
          ruleUid: rule?.uid || rule?._id || String(affectedRules.length),
          title: rule?.title,
          conditions: orphaned
        });
      });

      if (affectedRules.length === 0) return;

      impacted.push({
        fieldUid: field.uid,
        question: field.question || null,
        removed: Array.from(removed),
        rules: affectedRules
      });
    });

    return impacted;
  };

  const handleFormSubmit = async (data: ISESSION | IImages | IVideo | IMaps | ISign | IINSTRUCTIONS) => {
    if (!selectedNodeProp || disabled) return;

    try {
      const impacted = await findImpactedRules(data);

      if (impacted.length > 0) {
        pendingSubmitRef.current = data;
        setOptionImpact(impacted);
        return;
      }
    } catch (error) {
      // Advisory only. If the rules cannot be read the save must still go through — the
      // builder still flags an orphaned value when the rule is opened.
      console.error('Option impact check failed:', error);
    }

    commitFormData(data);
  };

  const handleOptionConfirm = () => {
    const pendingData = pendingSubmitRef.current;
    pendingSubmitRef.current = null;
    setOptionImpact([]);

    if (!pendingData) return;
    commitFormData(pendingData);
  };

  const handleOptionCancel = () => {
    pendingSubmitRef.current = null;
    setOptionImpact([]);

    // Discard the edit outright. The form was never committed to the node, so closing
    // here leaves the step exactly as it was: no step hash change, so no autosave, so
    // no request. Closing through this prop also skips the drawer's own onClose, which
    // would re-submit the form and raise this dialog again.
    onClose();
  };

  const handleStepTypeChange = (newType: string, shouldClearFields?: boolean) => {
    if (!selectedNodeProp || disabled) return;
    const updatedNode = {
      ...selectedNodeProp,
      data: {
        ...selectedNodeProp.data,
        step: {
          ...selectedNodeProp.data.step,
          stepType: newType
        },
        component: shouldClearFields ? {
          ...selectedNodeProp.data.component,
          fields: [] // Clear fields when step type changes
        } : selectedNodeProp.data.component
      }
    };
    handleStepComponentData(updatedNode.id, updatedNode.data);
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        // If disabled or a manager panel is shown, just close without saving
        if (disabled || extraNode === 'status' || extraNode === 'report') {
          onClose();
          return;
        }

        // If the rendered form exposes a `save` method, call it to trigger submit
        if (formRef.current && typeof formRef.current.save === 'function') {
          formRef.current.save();
          return;
        }

        // Fallback
        onClose();
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: '100%',
          maxWidth: '400px',
          ...(disabled && {
            pointerEvents: 'none',
            '& button': {
              opacity: 0.5,
              pointerEvents: 'none'
            }
          })
        }
      }}
    >
      <div style={{ 
        position: 'relative',
        height: '100%',
        ...(disabled && {
          pointerEvents: 'none',
          '& button': {
            opacity: 0.5,
            pointerEvents: 'none'
          }
        })
      }}>
        {extraNode === 'status' ? (
          <StatusTableManager onClose={onClose} />
        ) : extraNode === 'report' ? (
          <ReportTableManager onClose={onClose} />
        ) : (
          <RenderForm
            selectedNodeData={selectedNodeProp?.data?.component}
            selectedNode={selectedNodeProp}
            formRef={formRef}
            onSubmit={handleFormSubmit}
            onClose={onClose}
            onStepTypeChange={handleStepTypeChange}
            disabled={disabled}
          />
        )}
      </div>
    </Drawer>

    <OptionImpactDialog
      open={optionImpact.length > 0}
      impacted={optionImpact}
      onCancel={handleOptionCancel}
      onConfirm={handleOptionConfirm}
    />
    </>
  );
}
